// The app-detail shell (docs/design/slice3-knowledge-ux.md §2.2): redirects
// `/apps/:appId` to `/apps/:appId/explained`, fetches the app once (tenant-scoped,
// src/api/apps.ts:17-18 getApp), and renders AppShell + the Explained/Ask your crew
// tab strip around an Outlet. This is the ONE place cross-tenant denial is checked
// (§2.3): a not_found/forbidden here is byte-identical and renders the shared
// not-found card. Either child screen mounts only after this gate passes, so a
// knowledge-route error downstream (src/api/knowledge.ts) can never mean "not yours"
// again - see that file's own doc comment.
import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useParams } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import { AppId } from "@nightcrew/shared";
import type { AppView } from "@nightcrew/shared";
import { getApp } from "../api/apps.js";
import { isBoundaryCode } from "../api/errors.js";
import { AppShell } from "../components/AppShell.js";
import { DeniedCard } from "../components/DeniedCard.js";

export interface AppDetailContext {
  readonly appId: AppId;
  readonly app: AppView;
}

type LoadState =
  | { readonly status: "loading" }
  | { readonly status: "denied" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly app: AppView };

export const AppDetail = (): React.JSX.Element => {
  const { appId: rawAppId } = useParams<{ appId: string }>();
  const parsedAppId = AppId.safeParse(rawAppId);
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!parsedAppId.success) return;
    const appId = parsedAppId.data;
    const cancelled = { current: false };
    setState({ status: "loading" });
    void (async () => {
      const result = await getApp(appId);
      if (cancelled.current) return;
      if (isErr(result)) {
        const denied =
          isBoundaryCode(result.error, "not_found") || isBoundaryCode(result.error, "forbidden");
        setState(denied ? { status: "denied" } : { status: "error" });
        return;
      }
      setState({ status: "ready", app: result.value });
    })();
    return () => {
      cancelled.current = true;
    };
    // Keys on the raw param string (not `parsedAppId`, which is re-derived fresh every
    // render and would need its own memoization to be dependency-stable) plus the
    // retry counter.
  }, [rawAppId, attempt]);

  // A malformed appId in the URL can never mean "found" - fail closed to the same
  // shared not-found treatment rather than attempting a doomed API call.
  if (!parsedAppId.success) {
    return (
      <AppShell title="Your apps">
        <DeniedCard
          title="We can't find that app"
          message="It may not exist, or it isn't on your account."
        />
      </AppShell>
    );
  }

  if (state.status === "denied") {
    return (
      <AppShell title="Your apps">
        <DeniedCard
          title="We can't find that app"
          message="It may not exist, or it isn't on your account."
        />
      </AppShell>
    );
  }

  if (state.status === "error") {
    return (
      <AppShell title="Your apps">
        <div className="card state-card">
          <p>We could not load this app. Try again.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setAttempt((a) => a + 1);
            }}
          >
            Retry
          </button>
        </div>
      </AppShell>
    );
  }

  const title = state.status === "ready" ? state.app.name : "Your app";
  const appId = parsedAppId.data;

  return (
    <AppShell title={title}>
      {state.status === "loading" ? (
        <div className="stack gap12">
          <div className="skeleton skeleton-line" style={{ width: "30%" }} />
          <div className="card skeleton-card" />
          <div className="card skeleton-card" />
        </div>
      ) : (
        <>
          <nav className="app-subnav" aria-label={`${state.app.name} sections`}>
            <NavLink to={`/apps/${appId}/explained`} className="app-subtab" end>
              Explained
            </NavLink>
            <NavLink to={`/apps/${appId}/ask`} className="app-subtab">
              Ask your crew
            </NavLink>
            {/* Certify (docs/design/slice4-certify-ux.md §2.2) - unconditionally
                rendered, same as the two tabs above: the entry point is never hidden by
                tier or stage (§2.3); the destination screens gate their own content. */}
            <NavLink to={`/apps/${appId}/certify`} className="app-subtab">
              Certify
            </NavLink>
            {/* Build (docs/phase-0/slice5-ux-flows.md §2.4, §8.7) - the fourth sub-nav
                tab, unconditionally rendered exactly like the three above: `AppBuildTab`
                (the `build` child route, App.tsx) gates its own content by `app.stage`,
                never hidden here by tier or stage. */}
            <NavLink to={`/apps/${appId}/build`} className="app-subtab">
              Build
            </NavLink>
            {/* Slice 6 - Money / Pulse / Feedback (ADR-0023): unconditionally rendered like the
                tabs above; each screen gates its own content, never hidden here by tier or stage. */}
            <NavLink to={`/apps/${appId}/money`} className="app-subtab">
              Money
            </NavLink>
            <NavLink to={`/apps/${appId}/pulse`} className="app-subtab">
              Pulse
            </NavLink>
            <NavLink to={`/apps/${appId}/feedback`} className="app-subtab">
              Feedback
            </NavLink>
          </nav>
          <Outlet context={{ appId, app: state.app } satisfies AppDetailContext} />
        </>
      )}
    </AppShell>
  );
};

/** `/apps/:appId` (index) redirects to the Explained screen (ux spec §2.2). */
export const AppDetailIndexRedirect = (): React.JSX.Element => {
  const { appId } = useParams<{ appId: string }>();
  if (appId === undefined) return <Navigate to="/apps" replace />;
  return <Navigate to={`/apps/${appId}/explained`} replace />;
};
